import { useState } from 'react';
import Swal from 'sweetalert2';
import type { AdminPhoto, PhotoCategory } from '@/mocks/adminPhotos';
import { categoryLabels } from '@/mocks/adminPhotos';

interface PhotoGridProps {
  photos: AdminPhoto[];
  categories: PhotoCategory[];
  filterCategory: PhotoCategory | 'todas';
  onChangeCategory: (cat: PhotoCategory | 'todas') => void;
  filterAlbum: number | 'todos';
  onChangeAlbum: (id: number | 'todos') => void;
  albums: { id: number; name: string }[];
  onDelete: (photo: AdminPhoto) => void;
  onToggleFeatured: (photo: AdminPhoto) => void;
  onPhotoClick: (photo: AdminPhoto) => void;
  onEdit: (photo: AdminPhoto) => void;
  onShare: (photo: AdminPhoto) => void;
  onSetCover: (photo: AdminPhoto) => void;
}

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
  showConfirmButton: false,
  showCloseButton: true,
});

function showPhotoActionsModal(
  photo: AdminPhoto,
  handlers: {
    onEdit: (p: AdminPhoto) => void;
    onDelete: (p: AdminPhoto) => void;
    onToggleFeatured: (p: AdminPhoto) => void;
    onShare: (p: AdminPhoto) => void;
    onSetCover: (p: AdminPhoto) => void;
  }
) {
  const actionBtn = (icon: string, label: string, color: string, hoverBg: string) =>
    `<button type="button" data-action="${label.toLowerCase().replace(/\s/g, '-')}"
      class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-left transition-colors cursor-pointer ${color} ${hoverBg}">
      <span class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style="background:currentColor;opacity:0.1">
        <i class="${icon} text-base" style="color:currentColor"></i>
      </span>
      ${label}
    </button>`;

  const actions: string[] = [];

  actions.push(actionBtn('ri-pencil-line', 'Editar', 'text-foreground-700', 'hover:bg-background-100'));
  actions.push(actionBtn('ri-image-edit-line', 'Setear como portada', 'text-foreground-700', 'hover:bg-background-100'));
  actions.push(
    actionBtn(
      photo.isFeatured ? 'ri-star-line' : 'ri-star-fill',
      photo.isFeatured ? 'Quitar destacada' : 'Marcar destacada',
      'text-amber-600',
      'hover:bg-amber-50'
    )
  );
  actions.push(actionBtn('ri-share-forward-line', 'Compartir', 'text-sky-600', 'hover:bg-sky-50'));

  actions.push('<div class="border-t border-background-200 my-1"></div>');
  actions.push(actionBtn('ri-delete-bin-line', 'Eliminar', 'text-red-600', 'hover:bg-red-50'));

  sharedSwal
    .fire({
      title: photo.title,
      html: `
        <div class="text-left space-y-1">
          <p class="text-xs text-foreground-500 mb-2">${escapeHtml(photo.fileSize)} &bull; ${escapeHtml(categoryLabels[photo.category])}</p>
          <div class="flex items-center gap-2 mb-3 flex-wrap">
            ${photo.isFeatured
              ? '<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-700"><i class="ri-star-fill text-[9px]"></i>Destacada</span>'
              : '<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-background-100 text-foreground-500"><i class="ri-star-line text-[9px]"></i>Normal</span>'
            }
          </div>
          ${actions.join('')}
        </div>
      `,
      customClass: {
        popup: 'rounded-2xl max-w-sm',
        title: 'font-bold text-sm',
        htmlContainer: '!p-0',
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (!popup) return;

        popup.querySelector('[data-action="editar"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onEdit(photo);
        });
        popup.querySelector('[data-action="setear-como-portada"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onSetCover(photo);
        });
        popup.querySelector('[data-action="marcar-destacada"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onToggleFeatured(photo);
        });
        popup.querySelector('[data-action="quitar-destacada"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onToggleFeatured(photo);
        });
        popup.querySelector('[data-action="compartir"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onShare(photo);
        });
        popup.querySelector('[data-action="eliminar"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onDelete(photo);
        });
      },
    });
}

export default function PhotoGrid({
  photos,
  categories,
  filterCategory,
  onChangeCategory,
  filterAlbum,
  onChangeAlbum,
  albums,
  onDelete,
  onToggleFeatured,
  onPhotoClick,
  onEdit,
  onShare,
  onSetCover,
}: PhotoGridProps) {
  const [selectedPhotos, setSelectedPhotos] = useState<Set<number>>(new Set());

  const toggleSelect = (id: number) => {
    setSelectedPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedPhotos(new Set());

  return (
    <div>
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        {/* Categorías */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => onChangeCategory('todas')}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
              filterCategory === 'todas'
                ? 'bg-foreground-900 text-background-50'
                : 'bg-background-100 text-foreground-500 hover:bg-background-200'
            }`}
          >
            Todas
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => onChangeCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                filterCategory === cat
                  ? 'bg-foreground-900 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              {categoryLabels[cat]}
            </button>
          ))}
        </div>

        {/* Álbumes */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => onChangeAlbum('todos')}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
              filterAlbum === 'todos'
                ? 'bg-foreground-900 text-background-50'
                : 'bg-background-100 text-foreground-500 hover:bg-background-200'
            }`}
          >
            Todos álbumes
          </button>
          {albums.map((a) => (
            <button
              key={a.id}
              onClick={() => onChangeAlbum(a.id)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                filterAlbum === a.id
                  ? 'bg-foreground-900 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              {a.name}
            </button>
          ))}
        </div>

        {/* Selection actions */}
        {selectedPhotos.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-foreground-500 font-medium">
              {selectedPhotos.size} seleccionadas
            </span>
            <button
              onClick={() => {
                if (selectedPhotos.size === 0) return;
                Swal.fire({
                  title: '¿Eliminar fotos seleccionadas?',
                  html: `<p class="text-sm text-foreground-500">Se eliminarán permanentemente <strong>${selectedPhotos.size} foto${selectedPhotos.size !== 1 ? 's' : ''}</strong>. Esta acción no se puede deshacer.</p>`,
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Eliminar',
                  cancelButtonText: 'Cancelar',
                  confirmButtonColor: '#ef4444',
                  cancelButtonColor: '#9ca3af',
                  customClass: {
                    popup: 'rounded-2xl',
                    title: 'font-bold text-sm',
                    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                    htmlContainer: 'text-sm',
                  },
                  buttonsStyling: false,
                }).then((result) => {
                  if (result.isConfirmed) {
                    selectedPhotos.forEach((id) => onDelete(photos.find((p) => p.id === id)!));
                    clearSelection();
                    Swal.fire({
                      toast: true, position: 'top-end', title: `${selectedPhotos.size} foto${selectedPhotos.size !== 1 ? 's' : ''} eliminada${selectedPhotos.size !== 1 ? 's' : ''}.`,
                      icon: 'success', showConfirmButton: false, timer: 2500, timerProgressBar: true,
                      customClass: { popup: 'rounded-xl' },
                    });
                  }
                });
              }}
              className="px-3 py-1.5 rounded-lg bg-red-500 text-background-50 text-[10px] font-semibold hover:bg-red-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-[10px] mr-1"></i>
              Eliminar lote
            </button>
            <button
              onClick={clearSelection}
              className="text-[10px] font-semibold text-foreground-400 hover:text-foreground-600 cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      {photos.length === 0 ? (
        <div className="text-center py-14">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-3">
            <i className="ri-image-line text-foreground-400 text-xl"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-600">Sin fotos</p>
          <p className="text-xs text-foreground-400 mt-1">
            No hay fotos que coincidan con los filtros.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className={`group relative rounded-xl overflow-hidden bg-background-200 cursor-pointer transition-all ${
                selectedPhotos.has(photo.id) ? 'ring-2 ring-accent-500 ring-offset-2 ring-offset-background-50' : ''
              }`}
              onClick={() => {
                if (selectedPhotos.size > 0) {
                  toggleSelect(photo.id);
                } else {
                  onPhotoClick(photo);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                showPhotoActionsModal(photo, {
                  onEdit,
                  onDelete,
                  onToggleFeatured,
                  onShare,
                  onSetCover,
                });
              }}
            >
              <div className="aspect-square w-full">
                <img
                  src={photo.imageUrl}
                  alt={photo.title}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                <p className="text-background-50 text-xs font-semibold leading-tight mb-0.5 line-clamp-2">
                  {photo.title}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-background-50/70 text-[10px]">{photo.fileSize}</span>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        showPhotoActionsModal(photo, {
                          onEdit,
                          onDelete,
                          onToggleFeatured,
                          onShare,
                          onSetCover,
                        });
                      }}
                      className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer text-background-50/60 hover:text-background-50 hover:bg-background-50/20 transition-colors"
                      title="Acciones"
                    >
                      <i className="ri-more-2-fill text-sm"></i>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFeatured(photo);
                      }}
                      className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
                        photo.isFeatured ? 'text-amber-400 bg-amber-400/20' : 'text-background-50/60 hover:text-background-50 hover:bg-background-50/10'
                      }`}
                      title={photo.isFeatured ? 'Quitar destacada' : 'Destacar'}
                    >
                      <i className={`text-xs ${photo.isFeatured ? 'ri-star-fill' : 'ri-star-line'}`}></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Featured badge */}
              {photo.isFeatured && (
                <div className="absolute top-2 left-2 w-6 h-6 rounded-lg bg-amber-500/90 flex items-center justify-center">
                  <i className="ri-star-fill text-background-50 text-[10px]"></i>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}